package pl.nickers.match3.ecs

import kotlin.random.Random

// ---------------------------------------------------------------------------
// Canonical piece-id constants for the default piece set.
//
// During the transition (Phases 2–7 of the data-driven refactor), entity
// factories tag every entity they create with a [PieceIdComponent] using
// these ids. Once all systems read behaviour from piece definitions, the
// legacy factories and their marker components will be deleted.
// ---------------------------------------------------------------------------

object PieceIds {
    const val EMBER_SEED = "ember_seed"
    const val MOON_CRYSTAL = "moon_crystal"
    const val FOREST_HERB = "forest_herb"
    const val SUN_POLLEN = "sun_pollen"
    const val DUSK_PETAL = "dusk_petal"
    const val GOLDEN_NECTAR = "golden_nectar"
    const val BOMB = "bomb"
    const val CROSS_BOMB = "cross_bomb"
    const val LASER_HORIZONTAL = "laser_horizontal"
    const val LASER_VERTICAL = "laser_vertical"
    const val COLOURFUL_SWIRL = "colourful_swirl"
    const val CRYSTALLIZED_AMBER = "crystallized_amber"
    const val SPAWN_POINT = "spawn_point"
    const val TELEPORT_TARGET = "teleport_target"
    const val VOID_PUDDLE = "void_puddle"
    const val PURIFIED_RUNE = "purified_rune"
    const val POWERUP_BOMB = "powerup_bomb"
    const val POWERUP_ROW_BOMB = "powerup_row_bomb"
    const val POWERUP_TARGETED_REMOVE = "powerup_targeted_remove"
    const val FAMILIAR = "familiar"
}

/** Maps a legacy [JellyTypeComponent.type] int (1..6) to its canonical jelly piece id. */
fun pieceIdForJellyType(jellyType: Int): String = when (jellyType) {
    1 -> PieceIds.EMBER_SEED
    2 -> PieceIds.MOON_CRYSTAL
    3 -> PieceIds.FOREST_HERB
    4 -> PieceIds.SUN_POLLEN
    5 -> PieceIds.DUSK_PETAL
    6 -> PieceIds.GOLDEN_NECTAR
    else -> error("Unknown jelly type: $jellyType")
}

/** Inverse of [pieceIdForJellyType]: maps a jelly piece id to its legacy 1..6 type int. */
fun jellyTypeForPieceId(pieceId: String): Int = when (pieceId) {
    PieceIds.EMBER_SEED -> 1
    PieceIds.MOON_CRYSTAL -> 2
    PieceIds.FOREST_HERB -> 3
    PieceIds.SUN_POLLEN -> 4
    PieceIds.DUSK_PETAL -> 5
    PieceIds.GOLDEN_NECTAR -> 6
    else -> error("Unknown jelly piece id: $pieceId")
}

// ---------------------------------------------------------------------------
// Legacy entity factories. Each attaches a [PieceIdComponent] in addition to
// the existing marker components so the entity is forward-compatible with
// piece-def-driven systems landing in later phases.
// ---------------------------------------------------------------------------

fun World.createJellyEntity(
    row: Int,
    col: Int,
    jellyType: Int,
    random: Random,
): Int {
    val entityId = createEntity()
    addComponent(entityId, GridPositionComponent(row = row, col = col))
    addComponent(entityId, JellyTypeComponent(type = jellyType))
    addComponent(entityId, BodyImageComponent(image = "jelly_$jellyType.png"))
    addComponent(entityId, JellyFaceComponent(image = "face_${random.nextInt(1, 7)}.png"))
    addComponent(entityId, DraggableComponent())
    addComponent(entityId, PieceIdComponent(pieceIdForJellyType(jellyType)))
    return entityId
}

fun World.createBombEntity(
    row: Int,
    col: Int,
    random: Random,
): Int {
    val entityId = createEntity()
    addComponent(entityId, GridPositionComponent(row = row, col = col))
    addComponent(entityId, JellyTypeComponent(type = 0))
    addComponent(entityId, BodyImageComponent(image = "bomb.png"))
    addComponent(entityId, BombFaceComponent(image = "bomb_face_${random.nextInt(1, 3)}.png"))
    addComponent(entityId, DraggableComponent())
    addComponent(entityId, PieceIdComponent(PieceIds.BOMB))
    return entityId
}

fun World.createLaserEntity(
    row: Int,
    col: Int,
    direction: LaserDirection,
): Int {
    val entityId = createEntity()
    addComponent(entityId, GridPositionComponent(row = row, col = col))
    addComponent(entityId, JellyTypeComponent(type = 0))
    addComponent(entityId, BodyImageComponent(image = "laser.png"))
    addComponent(entityId, BombFaceComponent(image = "laser_face.png"))
    addComponent(entityId, DraggableComponent())
    addComponent(
        entityId,
        PieceIdComponent(
            if (direction == LaserDirection.HORIZONTAL) PieceIds.LASER_HORIZONTAL
            else PieceIds.LASER_VERTICAL,
        ),
    )
    return entityId
}

fun World.createCrossBombEntity(
    row: Int,
    col: Int,
    random: Random,
): Int {
    val entityId = createEntity()
    addComponent(entityId, GridPositionComponent(row = row, col = col))
    addComponent(entityId, JellyTypeComponent(type = 0))
    addComponent(entityId, BodyImageComponent(image = "cross_bomb.png"))
    addComponent(entityId, BombFaceComponent(image = "bomb_face_${random.nextInt(1, 3)}.png"))
    addComponent(entityId, DraggableComponent())
    addComponent(entityId, PieceIdComponent(PieceIds.CROSS_BOMB))
    return entityId
}

fun World.createIceCubeEntity(
    row: Int,
    col: Int,
    life: Int = 3,
): Int {
    val entityId = createEntity()
    addComponent(entityId, GridPositionComponent(row = row, col = col))
    addComponent(entityId, JellyTypeComponent(type = -1))
    addComponent(entityId, BodyImageComponent(image = "ice_${life.coerceIn(1, 3)}.png"))
    addComponent(entityId, IceCubeComponent(life = life))
    addComponent(entityId, PieceIdComponent(PieceIds.CRYSTALLIZED_AMBER))
    return entityId
}

fun World.createSpawnPointEntity(
    row: Int,
    col: Int,
    visible: Boolean = true,
): Int {
    val entityId = createEntity()
    addComponent(entityId, GridPositionComponent(row = row, col = col))
    addComponent(entityId, JellyTypeComponent(type = -2))
    addComponent(entityId, SpawnPointComponent(visible = visible))
    addComponent(entityId, PieceIdComponent(PieceIds.SPAWN_POINT))
    return entityId
}

/**
 * Creates an invisible marker entity at [destRow]/[destCol] that pulls a jelly from
 * [sourceRow]/[sourceCol] during the gravity teleport pass. The marker has only
 * [GridPositionComponent] + [TeleportTargetMarkerComponent] (no [JellyTypeComponent])
 * so it never conflicts with the jelly currently occupying the destination cell.
 */
fun World.createTeleportTargetEntity(
    destRow: Int,
    destCol: Int,
    sourceRow: Int,
    sourceCol: Int,
): Int {
    val entityId = createEntity()
    addComponent(entityId, GridPositionComponent(row = destRow, col = destCol))
    addComponent(entityId, TeleportTargetMarkerComponent(sourceRow = sourceRow, sourceCol = sourceCol))
    addComponent(entityId, PieceIdComponent(PieceIds.TELEPORT_TARGET))
    return entityId
}

/**
 * E5 - Void Puddle terrain tile at ([row], [col]). Terrain entities have no
 * [JellyTypeComponent], so they are invisible to [findEntityAt], match detection,
 * and gravity - pieces sit (and fall through) on top of them.
 */
fun World.createVoidPuddleEntity(row: Int, col: Int): Int {
    val entityId = createEntity()
    addComponent(entityId, GridPositionComponent(row = row, col = col))
    addComponent(entityId, VoidPuddleTerrainComponent())
    addComponent(entityId, PieceIdComponent(PieceIds.VOID_PUDDLE))
    return entityId
}

/** E5/E6 - Purified Rune terrain tile at ([row], [col]). Same shape as the void puddle. */
fun World.createPurifiedRuneEntity(row: Int, col: Int): Int {
    val entityId = createEntity()
    addComponent(entityId, GridPositionComponent(row = row, col = col))
    addComponent(entityId, PurifiedRuneComponent())
    addComponent(entityId, PieceIdComponent(PieceIds.PURIFIED_RUNE))
    return entityId
}

/**
 * Removes any teleport target marker(s) anchored at ([row], [col]). No-op if none exist.
 * Returns the number of markers deleted.
 */
fun World.removeTeleportTargetAt(row: Int, col: Int): Int {
    val posMapper = mapper<GridPositionComponent>()
    val toDelete = getEntitiesForAspect(
        Aspect.all(GridPositionComponent::class, TeleportTargetMarkerComponent::class),
    ).filter { eid ->
        val pos = posMapper[eid] ?: return@filter false
        pos.row == row && pos.col == col
    }
    toDelete.forEach { deleteEntity(it) }
    return toDelete.size
}

/**
 * Returns the source coordinates for a teleport marker anchored at ([row], [col]),
 * or null if no marker exists there.
 */
fun World.teleportSourceAt(row: Int, col: Int): Pair<Int, Int>? {
    val posMapper = mapper<GridPositionComponent>()
    val markerMapper = mapper<TeleportTargetMarkerComponent>()
    val eid = getEntitiesForAspect(
        Aspect.all(GridPositionComponent::class, TeleportTargetMarkerComponent::class),
    ).firstOrNull {
        val pos = posMapper[it] ?: return@firstOrNull false
        pos.row == row && pos.col == col
    } ?: return null
    val marker = markerMapper[eid] ?: return null
    return marker.sourceRow to marker.sourceCol
}

fun World.createColourfulSwirlEntity(
    row: Int,
    col: Int,
    jellyType: Int,
): Int {
    val entityId = createEntity()
    addComponent(entityId, GridPositionComponent(row = row, col = col))
    addComponent(entityId, JellyTypeComponent(type = jellyType))
    addComponent(entityId, BodyImageComponent(image = "swirl.png"))
    addComponent(entityId, BombFaceComponent(image = "swirl_face.png"))
    addComponent(entityId, DraggableComponent())
    addComponent(entityId, PieceIdComponent(PieceIds.COLOURFUL_SWIRL))
    return entityId
}

fun World.createEntityFromSelection(
    row: Int,
    col: Int,
    selection: EntitySelection,
): Int {
    val entityId = createEntity()
    addComponent(entityId, GridPositionComponent(row = row, col = col))
    addComponent(entityId, JellyTypeComponent(type = selection.typeId))
    addComponent(entityId, BodyImageComponent(image = selection.bodyImage))
    when (selection.type) {
        "bomb" -> {
            addComponent(entityId, BombFaceComponent(image = selection.faceImage))
        }
        else -> {
            addComponent(entityId, JellyFaceComponent(image = selection.faceImage))
        }
    }
    addComponent(entityId, DraggableComponent())
    addComponent(entityId, PieceIdComponent(selection.pieceId))
    return entityId
}

/**
 * Transforms an existing jelly entity in-place into a special tile, identified by
 * [pieceId]. The entity keeps its [GridPositionComponent] and [JellyTypeComponent]
 * but has its visual components and [PieceIdComponent] replaced from the piece
 * definition. All behaviour is driven from the new piece id via piece-def lookups.
 */
fun World.transformEntityToSpecialTile(
    entityId: Int,
    pieceId: String,
    pieceSet: PieceSet,
    random: Random,
) {
    val bodyMapper = mapper<BodyImageComponent>()
    val jellyFaceMapper = mapper<JellyFaceComponent>()
    val bombFaceMapper = mapper<BombFaceComponent>()

    // Clear old visual components.
    jellyFaceMapper.remove(entityId)
    bombFaceMapper.remove(entityId)
    // E5: special tiles are never cursed - a cursed jelly promoted to a special by a
    // match (e.g. the seed of a cursed 4-line) sheds its curse on transform.
    removeComponent(entityId, CursedComponent::class)

    val def = pieceSet.pieces[pieceId]
        ?: error("transformEntityToSpecialTile: piece '$pieceId' not found in '${pieceSet.id}'")
    when (def.family) {
        Family.BOMB -> {
            bodyMapper.set(entityId, BodyImageComponent("bomb.png"))
            addComponent(entityId, BombFaceComponent("bomb_face_${random.nextInt(1, 3)}.png"))
        }
        Family.CROSS_BOMB -> {
            bodyMapper.set(entityId, BodyImageComponent("cross_bomb.png"))
            addComponent(entityId, BombFaceComponent("bomb_face_${random.nextInt(1, 3)}.png"))
        }
        Family.LASER -> {
            bodyMapper.set(entityId, BodyImageComponent("laser.png"))
            addComponent(entityId, BombFaceComponent("laser_face.png"))
        }
        Family.SWIRL -> {
            bodyMapper.set(entityId, BodyImageComponent("swirl.png"))
            addComponent(entityId, BombFaceComponent("swirl_face.png"))
        }
        else -> error("transformEntityToSpecialTile: family ${def.family} not supported for in-place transform")
    }
    addComponent(entityId, PieceIdComponent(pieceId))
}

fun World.createBombPowerUpEntity(uses: Int = 1): Int {
    val entityId = createEntity()
    addComponent(entityId, PowerUpTypeComponent(PowerUpType.BOMB))
    addComponent(entityId, PowerUpUsesComponent(uses))
    addComponent(entityId, PieceIdComponent(PieceIds.POWERUP_BOMB))
    return entityId
}

fun World.createRowBombPowerUpEntity(uses: Int = 1): Int {
    val entityId = createEntity()
    addComponent(entityId, PowerUpTypeComponent(PowerUpType.ROW_BOMB))
    addComponent(entityId, PowerUpUsesComponent(uses))
    addComponent(entityId, PieceIdComponent(PieceIds.POWERUP_ROW_BOMB))
    return entityId
}

fun World.createTargetedRemovePowerUpEntity(uses: Int = 1): Int {
    val entityId = createEntity()
    addComponent(entityId, PowerUpTypeComponent(PowerUpType.TARGETED_REMOVE))
    addComponent(entityId, PowerUpUsesComponent(uses))
    addComponent(entityId, PieceIdComponent(PieceIds.POWERUP_TARGETED_REMOVE))
    return entityId
}

fun World.createFamiliarEntity(
    threshold: Int = 10,
    type: FamiliarType = FamiliarType.BLACK_CAT,
    triggerPieceId: String? = null,
): Int {
    val entityId = createEntity()
    addComponent(entityId, FamiliarComponent(
        progress = 0,
        threshold = threshold,
        type = type,
        triggerPieceId = triggerPieceId,
    ))
    addComponent(entityId, PieceIdComponent(PieceIds.FAMILIAR))
    return entityId
}

/**
 * Phase 4g: piece-id-driven power-up creation.
 *
 * Callers (GameViewModel) pick a power-up by [pieceId] instead of by [PowerUpType] enum.
 * Adding a new power-up type now only requires defining a new piece in the piece set
 * and one branch here.
 */
fun World.createPowerUpEntityByPieceId(pieceId: String, uses: Int = 1): Int = when (pieceId) {
    PieceIds.POWERUP_BOMB -> createBombPowerUpEntity(uses = uses)
    PieceIds.POWERUP_ROW_BOMB -> createRowBombPowerUpEntity(uses = uses)
    PieceIds.POWERUP_TARGETED_REMOVE -> createTargetedRemovePowerUpEntity(uses = uses)
    else -> error("createPowerUpEntityByPieceId: unknown power-up piece '$pieceId'")
}

/** Maps a legacy [PowerUpType] to its canonical piece id. */
fun pieceIdForPowerUpType(type: PowerUpType): String = when (type) {
    PowerUpType.BOMB -> PieceIds.POWERUP_BOMB
    PowerUpType.ROW_BOMB -> PieceIds.POWERUP_ROW_BOMB
    PowerUpType.TARGETED_REMOVE -> PieceIds.POWERUP_TARGETED_REMOVE
}

fun World.createRandomBoardEntity(
    row: Int,
    col: Int,
    random: Random,
    catalog: EntityCatalog,
): Int {
    val selection = catalog.selectRandom(random)
    return createEntityFromSelection(row = row, col = col, selection = selection)
}

// ---------------------------------------------------------------------------
// Phase 2: data-driven adapter factory.
//
// [createPieceEntity] is the entry point for piece-set-driven entity
// creation. It looks up [pieceId] in [pieceSet] and dispatches to the
// existing legacy factory appropriate for the piece's family, so the
// resulting entity is byte-identical to legacy code paths (same marker
// components, same JellyTypeComponent ints, same sprite filenames),
// plus the new [PieceIdComponent] for forward-looking systems.
//
// Later phases will rewrite this function to attach components directly
// from the piece definition and delete the legacy factories.
// ---------------------------------------------------------------------------

fun World.createPieceEntity(
    row: Int,
    col: Int,
    pieceId: String,
    pieceSet: PieceSet,
    random: Random,
): Int {
    val def = pieceSet.pieces[pieceId]
        ?: error("createPieceEntity: piece '$pieceId' not found in piece set '${pieceSet.id}'")
    return when (def.family) {
        Family.JELLY -> {
            val body = def.sprites.body
                ?: error("createPieceEntity: jelly piece '$pieceId' missing sprites.body")
            val type = jellyTypeForBody(body)
            createJellyEntity(row = row, col = col, jellyType = type, random = random)
        }
        Family.BOMB -> createBombEntity(row = row, col = col, random = random)
        Family.CROSS_BOMB -> createCrossBombEntity(row = row, col = col, random = random)
        Family.LASER -> {
            val params = def.params as PieceParams.Laser
            createLaserEntity(row = row, col = col, direction = params.direction)
        }
        Family.SWIRL -> {
            // Swirl currently requires a target jelly type at construction; that
            // assignment is decided by the match strategy that seeds it. When
            // seeded from a piece set (test level / sentinel), default to a
            // mid-range jelly type — the swap-resolve / double-click flow
            // overrides it at activation time anyway.
            createColourfulSwirlEntity(row = row, col = col, jellyType = 0)
        }
        Family.ICE -> {
            val params = def.params as PieceParams.Ice
            createIceCubeEntity(row = row, col = col, life = params.initialLife)
        }
        Family.INFRASTRUCTURE -> when (pieceId) {
            PieceIds.SPAWN_POINT -> createSpawnPointEntity(row = row, col = col)
            PieceIds.TELEPORT_TARGET -> error(
                "createPieceEntity: '$pieceId' requires source coords; " +
                        "use createTeleportTargetEntity directly",
            )
            else -> error("createPieceEntity: unknown infrastructure piece '$pieceId'")
        }
        Family.POWERUP -> when (pieceId) {
            PieceIds.POWERUP_BOMB -> createBombPowerUpEntity()
            PieceIds.POWERUP_ROW_BOMB -> createRowBombPowerUpEntity()
            PieceIds.POWERUP_TARGETED_REMOVE -> createTargetedRemovePowerUpEntity()
            else -> error("createPieceEntity: unknown power-up piece '$pieceId'")
        }
        Family.FAMILIAR -> {
            val params = def.params as PieceParams.Familiar
            createFamiliarEntity(threshold = params.threshold)
        }
    }
}

private fun jellyTypeForBody(body: String): Int = when (body) {
    "jelly_1.png" -> 1
    "jelly_2.png" -> 2
    "jelly_3.png" -> 3
    "jelly_4.png" -> 4
    "jelly_5.png" -> 5
    "jelly_6.png" -> 6
    else -> error("Unknown jelly body image: $body")
}
